import { CopyIcon, InfoIcon, StoreIcon, UtensilsIcon, XIcon } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Switch } from '@noor/ui';

const TIME_FIELD =
  'h-10 min-w-0 rounded-xl border-input bg-card px-3 text-sm tabular-nums shadow-xs focus-visible:border-input-border-focus';
const MESSAGE = 'min-h-0 px-1 empty:hidden';

interface ScheduleFieldProps {
  label: string;
}

/** Seven shared columns of the branch form, with a stacked row at phone widths. */
export function ScheduleField({ label }: ScheduleFieldProps) {
  const { t } = useTranslation('eats');
  const { control, getValues, setValue, trigger } = useFormContext();
  const { fields } = useFieldArray({ control, name: 'days' });

  const applyToAll = () => {
    const source = getValues('days.0') as {
      isOpen: boolean;
      open: string;
      close: string;
      orderFrom?: string;
      orderTo?: string;
    };
    if (!source) return;
    for (let index = 1; index < fields.length; index += 1) {
      for (const key of ['isOpen', 'open', 'close', 'orderFrom', 'orderTo'] as const) {
        setValue(`days.${index}.${key}`, source[key] ?? '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
    void trigger('days');
  };

  return (
    <section className="flex flex-col gap-4" aria-label={label}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{label}</h3>
          <p className="text-sm text-muted-foreground">
            {t('branches.form.scheduleDescription')}
          </p>
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={applyToAll}>
          <CopyIcon aria-hidden className="size-4" />
          {t('branches.form.applyToAllDays')}
        </Button>
      </div>

      <p className="mb-3 flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-sm text-muted-foreground">
        <InfoIcon aria-hidden className="mt-0.5 size-4 shrink-0 text-info" />
        {t('branches.form.orderWindowHint')}
      </p>
      <div className="rounded-xl border border-border bg-card">
        <div className="hidden grid-cols-[minmax(180px,0.62fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-border bg-muted/40 md:grid">
          <span className="px-4 py-3 text-sm font-medium text-foreground">
            {t('branches.form.weekdayHeading')}
          </span>
          <span className="flex items-center gap-2 border-l border-border px-4 py-3 text-sm font-medium text-foreground">
            <StoreIcon aria-hidden className="size-4 text-primary" />
            {t('branches.form.restaurantHoursHeading')}
          </span>
          <span className="flex items-center gap-2 border-l border-border px-4 py-3 text-sm font-medium text-foreground">
            <UtensilsIcon aria-hidden className="size-4 text-primary" />
            {t('branches.form.orderHoursHeading')}
          </span>
        </div>
        {fields.map((field, index) => (
          <DayRow
            key={field.id}
            index={index}
            label={(field as { label?: string }).label ?? ''}
          />
        ))}
      </div>
    </section>
  );
}

interface DayRowProps {
  index: number;
  label: string;
}

function DayRow({ index, label }: DayRowProps) {
  const { t } = useTranslation('eats');
  const { control, setValue, trigger } = useFormContext();
  const dayName = t(`venue.weekDaysFull.${label}`);
  const isOpen = useWatch({ control, name: `days.${index}.isOpen` }) as boolean;
  const orderFrom = useWatch({ control, name: `days.${index}.orderFrom` }) as string;
  const orderTo = useWatch({ control, name: `days.${index}.orderTo` }) as string;
  const orderAllDay = isOpen && !orderFrom && !orderTo;

  return (
    <div
      className="grid gap-3 border-b border-border p-3 last:border-b-0 md:grid-cols-[minmax(180px,0.62fr)_minmax(0,1fr)_minmax(0,1fr)] md:gap-0 md:p-0"
      data-testid={`schedule-day-${label}`}
    >
      <div className="flex items-center gap-2 md:px-3 md:py-3">
        <Switch
          checked={isOpen}
          onCheckedChange={(checked) =>
            setValue(`days.${index}.isOpen`, checked, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          aria-label={t('branches.form.dayToggle', { day: dayName })}
        />
        <span className="min-w-0 text-sm font-medium text-foreground">{dayName}</span>
      </div>

      <div className="min-w-0 md:border-l md:border-border md:px-4 md:py-3">
        <span className="mb-2 block text-xs font-medium text-muted-foreground md:hidden">
          {t('branches.form.restaurantHoursHeading')}
        </span>
        <div className="flex min-w-0 items-start gap-2">
          <FormField name={`days.${index}.open`} className="min-w-0 flex-1 gap-1">
            <FormField.Label className="sr-only">
              {t('branches.form.openLabel', { day: dayName })}
            </FormField.Label>
            <FormField.TimePicker className={TIME_FIELD} disabled={!isOpen} />
            <FormField.Message className={MESSAGE} />
          </FormField>
          <span className="pt-2 text-muted-foreground">—</span>
          <FormField name={`days.${index}.close`} className="min-w-0 flex-1 gap-1">
            <FormField.Label className="sr-only">
              {t('branches.form.closeLabel', { day: dayName })}
            </FormField.Label>
            <FormField.TimePicker
              className={TIME_FIELD}
              allowEndOfDay
              allow24HourEnd
              disabled={!isOpen}
            />
            <FormField.Message className={MESSAGE} />
          </FormField>
        </div>
      </div>

      <div className="min-w-0 md:border-l md:border-border md:px-4 md:py-3">
        <span className="mb-2 block text-xs font-medium text-muted-foreground md:hidden">
          {t('branches.form.orderHoursHeading')}
        </span>
        <div className="flex min-w-0 items-start gap-2">
          <FormField name={`days.${index}.orderFrom`} className="min-w-0 flex-1 gap-1">
            <FormField.Label className="sr-only">
              {t('branches.form.orderFrom', { day: dayName })}
            </FormField.Label>
            <FormField.TimePicker
              className={TIME_FIELD}
              disabled={!isOpen}
              placeholder="—:—"
            />
            <FormField.Message className={MESSAGE} />
          </FormField>
          <span className="pt-2 text-muted-foreground">—</span>
          <FormField name={`days.${index}.orderTo`} className="min-w-0 flex-1 gap-1">
            <FormField.Label className="sr-only">
              {t('branches.form.orderTo', { day: dayName })}
            </FormField.Label>
            <FormField.TimePicker
              className={TIME_FIELD}
              disabled={!isOpen}
              allowEndOfDay
              allow24HourEnd
              placeholder="—:—"
            />
            <FormField.Message className={MESSAGE} />
          </FormField>
        </div>
        {orderAllDay && (
          <span className="mt-1 block text-xs text-muted-foreground">
            {t('branches.form.orderAllDay')}
          </span>
        )}
        {!isOpen && (
          <span className="mt-1 block text-xs text-muted-foreground">
            {t('branches.form.dayClosed')}
          </span>
        )}
        {isOpen && !orderAllDay && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 h-8 text-xs"
            aria-label={t('branches.form.clearOrderHoursForDay', { day: dayName })}
            onClick={() => {
              setValue(`days.${index}.orderFrom`, '', { shouldDirty: true });
              setValue(`days.${index}.orderTo`, '', { shouldDirty: true });
              void trigger(`days.${index}`);
            }}
          >
            <XIcon aria-hidden className="size-3.5" />
            {t('branches.form.clearOrderHours')}
          </Button>
        )}
      </div>
    </div>
  );
}
